import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';

function MoneyScriptsPage() {
  const navigate = useNavigate();

  return (
    <div className="app-container">
      <div className="blue-gradient-overlay"></div>
      <div className="main-content money-scripts-content">
        <div className="money-scripts-container">
          <h1 className="money-scripts-title">What are money scripts?</h1>
          
          <div className="money-scripts-section-box">
            <p className="money-scripts-text">
              Money scripts are the deeply held beliefs we carry about money — often formed early in life and rarely questioned — that quietly shape how we earn, spend, save, give, and worry. They operate below conscious awareness, acting more like emotional reflexes than rational rules. You don't usually choose a money script; you inherit it, absorb it, or learn it through experience.
            </p>
            <p className="money-scripts-text">
              The concept of money scripts comes from the field of financial psychology and is most closely associated with the work of Brad Klontz and colleagues. Their research suggests that many financial behaviours — from chronic overspending to extreme frugality — are not driven by knowledge gaps, but by unconscious beliefs about what money means.
            </p>
          </div>

          <div className="money-scripts-section-box">
            <h2 className="money-scripts-heading">Where do money scripts come from?</h2>
            <p className="money-scripts-text">
              Money scripts typically form in childhood and adolescence, shaped by:
            </p>
            <ul className="money-scripts-list">
              <li>Family attitudes and behaviours around money</li>
              <li>Cultural narratives about success, security, and worth</li>
              <li>Early experiences of scarcity, abundance, instability, or shame</li>
              <li>Emotional moments involving money (conflict, fear, reward, control)</li>
            </ul>
            <p className="money-scripts-text">
              Because money is rarely discussed openly — and often emotionally charged — these beliefs tend to go unexamined. Over time, they harden into "truths" that feel self-evident: Money is dangerous, More money will fix everything, I don't deserve wealth, Talking about money is rude.
            </p>
          </div>

          <div className="money-scripts-section-box">
            <h2 className="money-scripts-heading">The four common money script patterns</h2>
            <p className="money-scripts-text">
              Research has identified several recurring clusters of money beliefs. While individuals usually hold a mix, one pattern often dominates:
            </p>
            
            <div className="money-script-pattern">
              <h3 className="money-script-pattern-title">Money Avoidance</h3>
              <p className="money-scripts-text">
                Beliefs that money is bad, corrupting, or undeserved. Often linked to avoidance of financial decisions, under-earning, or anxiety around wealth.
              </p>
            </div>

            <div className="money-script-pattern">
              <h3 className="money-script-pattern-title">Money Worship</h3>
              <p className="money-scripts-text">
                The belief that more money will solve problems or bring happiness. Commonly associated with overspending, risk-taking, or perpetual dissatisfaction.
              </p>
            </div>

            <div className="money-script-pattern">
              <h3 className="money-script-pattern-title">Money Status</h3>
              <p className="money-scripts-text">
                Viewing net worth as a measure of personal value or success. Can lead to comparison, lifestyle inflation, and financial fragility despite high income.
              </p>
            </div>

            <div className="money-script-pattern">
              <h3 className="money-script-pattern-title">Money Vigilance</h3>
              <p className="money-scripts-text">
                Strong focus on saving, caution, and preparedness. Often adaptive, but can tip into chronic anxiety, guilt, or difficulty enjoying money.
              </p>
            </div>

            <p className="money-scripts-text">
              These scripts are not "good" or "bad" in themselves. Each can be protective in some contexts and limiting in others. Problems arise when a script runs automatically, without awareness or flexibility.
            </p>
          </div>

          <div className="money-scripts-section-box">
            <h2 className="money-scripts-heading">Why money scripts matter</h2>
            <p className="money-scripts-text">
              Studies using money-script inventories have found meaningful links between these beliefs and real financial outcomes — including income, net worth, debt levels, and financial stress. In other words, how you think about money is often as influential as how much you earn.
            </p>
            <p className="money-scripts-text">
              Crucially, money scripts help explain a common frustration:
            </p>
            <p className="money-scripts-quote">
              "I know what I should do with money — so why don't I do it?"
            </p>
            <p className="money-scripts-text">
              From this perspective, behaviour change isn't just about better tools or advice. It requires understanding the emotional logic underneath decisions — the beliefs that make certain choices feel safe, threatening, virtuous, or forbidden.
            </p>
          </div>

          <div className="money-scripts-section-box">
            <h2 className="money-scripts-heading">Can money scripts change?</h2>
            <p className="money-scripts-text">
              Yes — but not through willpower alone. Because money scripts are often subconscious, change begins with awareness. Naming a belief creates distance from it. Once visible, a script can be questioned, softened, or rewritten.
            </p>
            <p className="money-scripts-text">
              Rather than aiming to eliminate money scripts, the goal is to relate to them more consciously — keeping what's useful, loosening what's limiting, and making room for more intentional financial choices.
            </p>
            <p className="money-scripts-text money-scripts-final">
              Money scripts are not destiny. They are stories. And stories, once seen clearly, can change.
            </p>
          </div>

          <div className="money-scripts-actions">
            <button 
              className="secondary-button"
              onClick={() => navigate(-1)}
            >
              Back to results
            </button>
            <button 
              className="primary-button"
              onClick={() => {
                // Download the research paper PDF
                const link = document.createElement('a');
                link.href = `${process.env.PUBLIC_URL}/data/Money_Beliefs_and_Financial_Behaviors_Development_.pdf`;
                link.download = 'Money_Beliefs_and_Financial_Behaviors_Development.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download the research paper
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MoneyScriptsPage;

